import { createContext, useContext, useEffect, useState } from "react";
import ImagesService from "../services/ImagesService";
import { enhanceImageQuality } from "../components/Image/Image";
import { Buffer } from "buffer";
import { useSSEContext } from "./sse";
import { useAuth } from "./auth";

const IndexedDbContext = createContext({
	saveImage: null,
	getImage: null,
	lastSessionData: null,
});

const DB_NAME = 'bravasCloudDB';
export const IMAGES_STORE = 'imagesStore';
export const SESSION_STORE = 'lastSessionData';
const STORES = [IMAGES_STORE, SESSION_STORE];

export const IndexedDbProvider = ({ children }) => {
	const [lastSessionData, setLastSessionData] = useState(null);
	const { sseReceived } = useSSEContext();
	const { user } = useAuth();

	useEffect(() => {
		(async () => {
			const data = await get(SESSION_STORE, SESSION_STORE);
			if (data) {
				setLastSessionData({ ...lastSessionData, ...data });
			}
		})();
	}, []);

	useEffect(() => {
		async function onSseReceived() {
			if (sseReceived?.action === "organizationLogoUpdated" && sseReceived.content.newUrlLogo && user.userData?.selectedOrganization?.externalId === sseReceived.content?.organizationExternalId) {
				saveImage('organization-logo', sseReceived.content.newUrlLogo);
			}
		}
		onSseReceived();
	}, [sseReceived]);

	function open() {
		return new Promise((resolve, reject) => {
			const request = indexedDB.open(DB_NAME, 1);

			request.onupgradeneeded = (event) => {
				const db = event.target.result;
				for (const storeName of STORES) {
					if (!db.objectStoreNames.contains(storeName)) {
						db.createObjectStore(storeName, { keyPath: 'id' });
					}
				}
			};

			request.onsuccess = (event) => {
				const db = event.target.result;
				resolve(db);
			};

			request.onerror = (event) => {
				reject(event.target.error);
			};
		});
	}

	async function save(record, storeName) {
		let lastData = await get(record.id, storeName);
		const db = await open();
		const transaction = db.transaction([storeName], 'readwrite');
		const store = transaction.objectStore(storeName);
		let newRecord = record;
		if (storeName === SESSION_STORE) {
			newRecord = { ...lastData, ...lastSessionData, ...record }
			setLastSessionData(newRecord);
		}
		store.put(newRecord);
		return transaction.complete;
	}

	async function saveImage(id, url, storeName = IMAGES_STORE) {
		const blob = await loadRemoteImage(url);
		const imageRecord = { id, url, blob, timestamp: Date.now() };
		const complete = await save(imageRecord, storeName);
		await save({ id: SESSION_STORE, organizationLogo: url }, SESSION_STORE);
		return complete;
	}

	async function loadRemoteImage(url) {
		if (!url) return;
		try {
			const response = await ImagesService.getImage(url);
			if (!response.data) return;
			const responseImage = await ImagesService.getRawImage(response.data.replace("[:STORAGE_ADDRESS:]", window.location.hostname));
			const binaryData = responseImage.data;
			const base64Data = new Buffer.from(binaryData, "binary").toString(
				"base64",
			);
			const originalDataUrl = `data:image/png;base64,${base64Data}`;
			const enhancedBlob = await enhanceImageQuality(originalDataUrl, true);
			return enhancedBlob;
		} catch (error) {
			console.error("Erro ao carregar imagem do servidor:", error);
		}
	}

	async function get(id, storeName) {
		const db = await open();
		return new Promise((resolve, reject) => {
			try {
				const transaction = db.transaction([storeName], 'readonly');
				const store = transaction.objectStore(storeName);
				const request = store.get(id);
				request.onsuccess = (event) => {
					const record = event.target.result;
					if (record) {
						resolve(record);
					} else {
						resolve(null);
					}
				};
				request.onerror = (event) => {
					reject(event.target.error);
				};
			} catch (error) {
				reject(error);
			}
		});
	}

	async function getImage(id, storeName = IMAGES_STORE) {
		const record = await get(id, storeName);
		if (record?.blob) {
			return URL.createObjectURL(record.blob);
		}
	}

	async function loadImage(id, url, storeName = IMAGES_STORE) {
		if (!id) return null;
		let objectURL = await getImage(id, storeName);
		if (objectURL) {
			return objectURL;
		} else if (url) {
			await saveImage(id, url, storeName);
			objectURL = await getImage(id, storeName);
			return objectURL;
		}
	}

	async function remove(id, storeName = IMAGES_STORE) {
		const db = await open();
		const transaction = db.transaction([storeName], 'readwrite');
		const store = transaction.objectStore(storeName);
		store.delete(id);
		if (storeName === IMAGES_STORE) {
			setLastSessionData({ ...lastSessionData, organizationLogo: null });
		}
		return transaction.complete;
	}

	async function clearStore(storeName = IMAGES_STORE) {
		const db = await open();
		const transaction = db.transaction([storeName], 'readwrite');
		const store = transaction.objectStore(storeName);
		store.clear();
		return transaction.complete;
	}

	return (
		<IndexedDbContext.Provider value={{ loadImage, remove, saveImage, save, get, lastSessionData }}>
			{children}
		</IndexedDbContext.Provider>
	);
}

export function useIndexedDb() {
	const context = useContext(IndexedDbContext);
	return context;
}